import { useState, useEffect } from "react";
import toast from "react-hot-toast";

interface ConfirmationFormProps {
  email: string;
  onConfirm: (code: string) => void;
  onResendCode: () => void;
  error?: string | null;
}

export const ConfirmationForm = ({
  email,
  onConfirm,
  onResendCode,
  error,
}: ConfirmationFormProps) => {
  const [confirmationCode, setConfirmationCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsVerifying(true);
    try {
      await onConfirm(confirmationCode);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    try {
      await onResendCode();
      toast.success("Verification code has been resent to your email");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
      <h2 className="text-center text-2xl font-bold text-white mb-6">
        Enter Confirmation Code
      </h2>
      <p className="text-center text-gray-300 mb-6">
        A confirmation code has been sent to {email}. Please enter it below.
      </p>
      <div className="text-xs text-orange-500">
        If you don't see the email in your inbox, please check your junk or spam folder.
      </div>
      <div>
        <label
          htmlFor="confirmationCode"
          className="block text-sm font-medium text-gray-300 mb-1"
        >
          Confirmation Code
        </label>
        <input
          id="confirmationCode"
          name="confirmationCode"
          type="text"
          required
          value={confirmationCode}
          onChange={(e) => setConfirmationCode(e.target.value)}
          className="block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-gray-700 text-white text-sm"
          placeholder="Enter your confirmation code"
        />
        {error && (
          <span className="text-sm text-red-400 font-semibold mt-1 block">
            {error}
          </span>
        )}
      </div>
      <div className="space-y-3">
        <button
          type="submit"
          disabled={isVerifying}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
        >
          {isVerifying ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Verifying...
            </>
          ) : (
            "Verify Account"
          )}
        </button>
        <button
          type="button"
          onClick={handleResendCode}
          disabled={isResending}
          className="w-full flex justify-center py-2 px-4 border border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
        >
          {isResending ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-300"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Resending...
            </>
          ) : (
            "Resend Code"
          )}
        </button>
      </div>
    </form>
  );
};

